<template>
  <el-container style="height: 100vh;">
    <!-- PC端侧边栏 -->
    <el-aside 
      v-show="!isMobile"
      width="240px" 
      style="background-color: #304156; position: fixed; left: 0; top: 0; bottom: 0; z-index: 1001;"
    >
      <!-- 原有PC端侧边栏内容保持不变 -->
      <div class="user-info">
        <img :src="userInfo.avatar" class="user-avatar">
        <div class="user-name">
          {{ userInfo.name }}
          <el-tooltip content="退出登录" placement="top">
            <el-button
              type="text"
              icon="el-icon-switch-button"
              @click="handleLogout"
              class="logout-btn"
            ></el-button>
          </el-tooltip>
        </div>
      </div>

      <el-menu
        :default-active="activeMenu"
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
        @select="handleMenuSelect"
      >
        <el-menu-item 
          v-for="item in menuConfig"
          :key="item.name"
          :index="item.name"
        >
          <i :class="item.icon"></i>
          <span>{{ item.title }}</span>
        </el-menu-item>
      </el-menu>
    </el-aside>

    <!-- 移动端顶部导航 -->
    <div v-show="isMobile" class="mobile-header">
      <div class="mobile-nav">
        <el-button 
          class="menu-toggle"
          type="text" 
          icon="el-icon-menu"
          @click="showMobileMenu = true"
        />
        <div class="mobile-title">{{ currentTabTitle }}</div>
        <el-button
          class="logout-btn"
          type="text" 
          icon="el-icon-switch-button"
          @click="handleLogout"
        />
      </div>
      
      <!-- 移动端菜单抽屉 -->
      <el-drawer
        title="菜单"
        :visible.sync="showMobileMenu"
        direction="ltr"
        :modal="false" 
        size="75%"
      >
        <el-menu
          :default-active="activeMenu"
          @select="handleMobileMenuSelect"
        >
          <el-menu-item 
            v-for="item in menuConfig"
            :key="item.name"
            :index="item.name"
          >
            <i :class="item.icon"></i>
            <span>{{ item.title }}</span>
          </el-menu-item>
        </el-menu>
      </el-drawer>
    </div>

    <!-- 右侧内容区 -->
    <el-container :style="mainContainerStyle">
      <!-- PC端标签栏 -->
      <el-header 
        v-show="!isMobile"
        class="tabs-header" 
        style="position: fixed; top: 0; right: 0; left: 240px; z-index: 1000;"
      >
        <el-tabs
          v-model="activeTab"
          type="card"
          closable
          @tab-remove="removeTab"
        >
          <el-tab-pane
            v-for="tab in tabs"
            :key="tab.name"
            :name="tab.name"
          >
            <template #label>
              <span>{{ tab.title }}</span>
            </template>
          </el-tab-pane>
        </el-tabs>
      </el-header>

      <!-- 内容区域 -->
      <el-main class="content-main" :style="contentMainStyle">
        <component 
          v-for="tab in tabs"
          :key="tab.name"
          :is="tab.component"
          v-show="activeTab === tab.name"
          class="tab-component"
        />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import menuConfig from '@/config/menu'
export default {
  data() {
    return {
      userInfo: {
        name: '管理员',
        avatar: require('@/assets/default_user.png')
      },
      menuConfig,
      activeMenu: '',
      activeTab: '',
      tabs: [],
      isMobile: false,
      showMobileMenu: false
    }
  },
  computed: {
    mainContainerStyle() {
      return this.isMobile ? { 'margin-left': '0' } : { 'margin-left': '240px' }
    },
    contentMainStyle() {
      return this.isMobile ? 
        { 'margin-top': '50px', height: 'calc(100vh - 50px)' } :
        { 'margin-top': '40px', height: 'calc(100vh - 40px)' }
    },
    currentTabTitle() {
      const tab = this.tabs.find(t => t.name === this.activeTab)
      return tab ? tab.title : '首页'
    }
  },
  created() {
    // 保持原有初始化逻辑
    const username = localStorage.getItem('username')
    this.userInfo.name = username
    const openedTabs = this.$route.query.openedTabs?.split(',') || []
    const activeTab = this.$route.query.activeTab
    
    const validTabs = openedTabs
      .map(name => this.menuConfig.find(item => item.name === name))
      .filter(Boolean)
    
    const defaultTab = this.menuConfig.find(item => !item.closable)
    if (defaultTab && !validTabs.find(t => t.name === defaultTab.name)) {
      validTabs.push(defaultTab)
    }

    this.tabs = validTabs.map(tab => ({
      ...tab,
      closable: tab.closable !== false
    }))

    const initialTab = activeTab && validTabs.find(t => t.name === activeTab)
      ? activeTab
      : defaultTab?.name || ''
    
    this.activeTab = initialTab
    this.activeMenu = initialTab
    this.syncUrlParams()
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    handleMenuSelect(name) {
      const menuItem = this.menuConfig.find(item => item.name === name)
      if (!menuItem) return

      // 强制刷新当前页逻辑
      if (name === this.activeTab) {
        const index = this.tabs.findIndex(tab => tab.name === name)
        this.tabs[index] = {
          ...this.tabs[index],
          timestamp: Date.now()
        }
      }

      // 添加新标签页逻辑
      const existingTab = this.tabs.find(tab => tab.name === name)
      if (!existingTab) {
        this.tabs.push({
          ...menuItem,
          closable: menuItem.closable !== false,
          timestamp: Date.now()
        })
      }

      this.activeTab = name
      this.syncUrlParams()
    },
    handleMobileMenuSelect(name) {
      this.showMobileMenu = false
      this.handleMenuSelect(name)
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    // 新增方法：同步所有URL参数
    syncUrlParams() {
      const newParams = {
        activeTab: this.activeTab,
        openedTabs: this.tabs.map(t => t.name).join(',')
      }

      if (JSON.stringify(newParams) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({
          query: newParams
        }).catch(() => {})
      }
    },
    removeTab(targetName) {
      if (this.tabs.length <= 1) return
      
      const tabs = this.tabs.filter(tab => tab.name !== targetName)
      const activeTab = tabs.length ? tabs[tabs.length - 1].name : ''
      
      this.tabs = tabs
      this.activeTab = activeTab
      this.syncUrlParams()
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 这里实际应调用退出接口
        // 示例：跳转到登录页
        localStorage.removeItem('token')
        this.$router.push('/login')
        this.$message.success('已退出登录')
      }).catch(() => {})
    },
  }
}
</script>

<style scoped>

/* 新增样式 */
.tab-component {
  background: white;
  padding: 20px;
  border-radius: 4px;
}


.tabs-header {
  height: 40px !important;
  padding: 0 20px;
  background: white;
  border-bottom: 1px solid #e6e6e6;
}

.content-main {
  padding: 20px;
  background-color: #f0f2f5;
}

.user-info {
  padding: 20px 0;
  text-align: center;
  border-bottom: 1px solid #263445;
}

.user-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-bottom: 10px;
  border: 2px solid #fff;
}

.user-name {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
}

.el-aside {
  overflow: hidden;
}

.el-menu {
  border-right: none;
}

.el-tabs {
  user-select: none;
}

.tab-content {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  min-height: calc(100vh - 100px);
}

.el-header {
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
}

/* 新增移动端样式 */
@media screen and (max-width: 768px) {
  .mobile-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: #304156;
    z-index: 1001;
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  }

  .mobile-nav {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 10px;
    color: white;
  }

  .menu-toggle {
    color: white !important;
    font-size: 24px;
    margin-right: 15px;
  }

  .mobile-title {
    flex: 1;
    font-size: 18px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .logout-btn {
    color: white !important;
    font-size: 24px;
    margin-left: 15px;
  }

  .content-main {
    padding: 10px !important;
  }

  .tab-component {
    padding: 10px !important;
    min-height: calc(100vh - 60px);
  }

  .el-drawer__header {
    margin-bottom: 0;
    padding: 20px;
    background: #304156;
    color: white;
  }

  .el-drawer__body {
    padding: 0;
  }

  .el-menu {
    border-right: none;
  }

  .el-menu-item {
    height: 50px;
    line-height: 50px;
  }
}
</style>